/**
 * @fileoverview ACTIVE ACCELERATOR COMPONENT FOR COMPANIES TABLE VIEW
 * 
 * THIS IS THE CURRENT ACTIVE IMPLEMENTATION FOR DISPLAYING ACCELERATOR INFORMATION IN COMPANY TABLE.
 * It displays the accelerator logo (Y Combinator, Techstars, etc.) and slug information for a company
 * in the table view of the /companies page.
 * 
 * @active CONFIRMED - This component is actively used in the application
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/CompaniesInfoTableRow.tsx} - Parent component that uses this in table view
 * @see {@link src/components/ui/buttons/acceleratorSelector.tsx} - Utility for selecting accelerator icons (Y Combinator, Techstars, etc.)
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorCardView.tsx} - Version used in card view
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesCards/companyCardItem/AcceleratorDetailPage.tsx} - Version used in company detail pages
 */

import { Fragment } from "react";

import { Box, Flex, Text } from "@chakra-ui/react";

import { acceleratorSelector } from "@/components/ui/buttons/acceleratorSelector";
import { h_400_12_135_48 } from "@/styles/fontStyles";

/**
 * Company data interface with accelerator information
 */
interface CompanyWithAccelerator {
  id?: string;
  accelerator?: Array<{ id: string; slug: string }> | { id: string; slug: string; incubator_name?: string };
  [key: string]: any;
}

/**
 * Props for the AcceleratorListView component
 */
interface AcceleratorListViewProps {
  /**
   * Company data containing accelerator information
   */
  item: CompanyWithAccelerator;
  
  /**
   * Maximum width for the component
   */
  maxWidth?: string | number;
}

/**
 * Active accelerator component that displays accelerator information in company table
 * Used in the table view of the /companies page
 * 
 * @param {AcceleratorListViewProps} props - Component props
 * @returns {JSX.Element|null} Rendered component or null if no accelerator data
 */
const AcceleratorListView = ({ 
  item, 
  maxWidth = "auto" 
}: AcceleratorListViewProps) => {
  // Return null if no accelerator data
  if (!item?.accelerator && !item?.initialData?.accelerator) {
    return null;
  }
  
  // Debug the incoming data structure
  console.log('AcceleratorListView received item:', {
    itemId: item?.id,
    accelerator: item?.accelerator,
    acceleratorInInitialData: item?.initialData?.accelerator,
    acceleratorType: item?.accelerator ? (Array.isArray(item.accelerator) ? 'array' : typeof item.accelerator) : 'undefined',
    keys: item?.accelerator ? Object.keys(item.accelerator) : []
  });

  // Prepare the company data in the format expected by acceleratorSelector
  // The key is to use the correct structure that acceleratorSelector expects
  const companyData = {
    initialData: {
      accelerator: item?.initialData?.accelerator || item?.accelerator,
      webUrl: item?.initialData?.webUrl || item?.webUrl
    }
  };

  // Get the accelerator icon with debug logging
  console.log('Calling acceleratorSelector with:', companyData);
  // Use type assertion to fix TypeScript error
  const accelerator = acceleratorSelector({ company: companyData as any });
  console.log('acceleratorSelector returned:', accelerator ? 'Icon component' : 'null');
  
  // Extract the accelerator name/slug
  let acceleratorName = "";
  const acceleratorData = item?.initialData?.accelerator || item?.accelerator;
  
  if (Array.isArray(acceleratorData)) {
    acceleratorName = acceleratorData[0]?.slug || "";
  } else if (typeof acceleratorData === 'object' && acceleratorData !== null) {
    acceleratorName = acceleratorData.incubator_name || acceleratorData.slug || "";
  }

  return (
    <Fragment>
      {accelerator && (
        <Flex
          w="100%"
          maxW={maxWidth}
          align="center"
          justify="center" // Center horizontally
          overflow="hidden"
          title={acceleratorName} // Add title for accessibility
          aria-label={`Accelerator: ${acceleratorName}`} // Add aria-label for screen readers
          h="100%" // Full height
          py="8px" // Add vertical padding
        >
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            minW="90px" // Increased minimum width to ensure logo has space
            minH="24px" // Minimum height to ensure logo has space
            sx={{
              "& svg": {
                minWidth: "75px", // Ensure SVGs have minimum width
                height: "auto", // Maintain aspect ratio
                maxHeight: "18px" // Match the height of the SVG viewBox
              }
            }}
          >
            {accelerator}
          </Box>
        </Flex>
      )}
    </Fragment>
  );
};

// For backward compatibility with existing imports
export const AcceleratorDesktop = AcceleratorListView;

export default AcceleratorListView;
